import { Api } from '@/api/api.client';
import { Components } from '@/api/generated/api';
import { clearLocalSession } from '@/auth/clearLocalSession';
import { useToast } from '@/composables/useToast';
import { useAuthStore } from '@/stores/useAuthStore';
import { assertNotNullOrUndefined } from '@/utils/assertion.utils';
import { computed, ref, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

export type TfaOption = 'totp' | 'email-otp';

type AuthAttemptStatus =
  | 'init'
  | 'tfaRequired'
  | 'successForSameDevice'
  | 'successForAllDevices'
  | 'failed'
  | 'used'
  | 'expired';

type LocalLoginResponse = {
  success: boolean;
  status: AuthAttemptStatus;
  csrfToken: string;
};

type LocalLoginTfaResponse = {
  success: boolean;
  status: AuthAttemptStatus;
  tfaOptions: TfaOption[];
  authAttempt: AuthAttempt;
};

type AuthAttempt = {
  id: string;
  secret: string;
};

type LoginResult =
  | { ok: true; requiresTfa: false }
  | { ok: true; requiresTfa: true; tfaOptions: TfaOption[] }
  | { ok: false };

interface IUseAuthComposable {
  isLoggedIn: Ref<boolean>;
  login: (userIdentifier: string, password: string) => Promise<LoginResult>;
  logout: (options?: { redirect?: boolean }) => Promise<void>;
  completeTfa: (method: TfaOption, tfaCode: string) => Promise<void>;
  resendEmailOtp: (showToast?: boolean) => Promise<void>;
}

export function useAuth(): IUseAuthComposable {
  const { push } = useRouter();
  const { customSuccessToast, catchError } = useToast();
  const { t } = useI18n();

  const authStore = useAuthStore();

  const pendingAuthAttempt = ref<AuthAttempt | null>(null);
  const pendingTfaOptions = ref<TfaOption[] | null>(null);

  async function login(userIdentifier: string, password: string): Promise<LoginResult> {
    const dto: Components.Schemas.LocalLoginDto = {
      userIdentifier,
      password,
      redirectUri: '' as any,
    };

    const response = (await Api.Routes.LocalLogin.login(null, dto)) as LocalLoginResponse | LocalLoginTfaResponse;

    if (isTfaResponse(response)) {
      pendingAuthAttempt.value = response.authAttempt;
      pendingTfaOptions.value = response.tfaOptions;
      Api.setHeader('x-csrf-token', null);
      return { ok: true, requiresTfa: true, tfaOptions: pendingTfaOptions.value };
    }

    authStore.login(response.csrfToken);
    Api.setHeader('x-csrf-token', response.csrfToken);
    return { ok: true, requiresTfa: false };
  }

  async function completeTfa(method: TfaOption, tfaCode: string): Promise<void> {
    assertNotNullOrUndefined(pendingAuthAttempt.value);

    // ugly method to silence invalid type warning...
    const dto = { tfaCode, authAttempt: pendingAuthAttempt.value } as unknown as Components.Schemas.TfaLoginDto;

    let res: LocalLoginResponse | null = null;
    if (method === 'totp') {
      res = (await Api.Routes.LocalLogin.loginTotp(null, dto)) as LocalLoginResponse;
    } else {
      res = (await Api.Routes.LocalLogin.loginEmailOtp(null, dto)) as LocalLoginResponse;
    }

    authStore.login(res.csrfToken);
    Api.setHeader('x-csrf-token', res.csrfToken);
    pendingAuthAttempt.value = null;
    pendingTfaOptions.value = null;
  }

  async function resendEmailOtp(showToast?: boolean): Promise<void> {
    assertNotNullOrUndefined(pendingAuthAttempt.value);

    const dto = { authAttempt: pendingAuthAttempt.value };
    await catchError(async () => {
      await Api.Routes.EmailOtp.sendEmail(null, dto);
      if (showToast) customSuccessToast(t('auth.login.resendEmailSuccess'));
    });
  }

  async function logout(options?: { redirect?: boolean }): Promise<void> {
    try {
      await Api.Routes.CoreAuth.logout();
    } finally {
      await clearLocalSession();
      if (options?.redirect !== false) {
        void push('/auth/login');
      }
    }
  }

  function isTfaResponse(res: LocalLoginResponse | LocalLoginTfaResponse): res is LocalLoginTfaResponse {
    return 'tfaOptions' in res && 'authAttempt' in res;
  }

  const isLoggedIn = computed(() => authStore.csrfToken != null);

  return {
    login,
    logout,
    isLoggedIn,
    completeTfa,
    resendEmailOtp,
  };
}
